import { useSyncExternalStore } from "react";
import { readReferral } from "./referral";

/**
 * Enlace de registro que lleva la última prueba de la herramienta gratuita.
 *
 * Primera versión (28-9-2026): la prueba se guardaba en sessionStorage y la URL se
 * construía en el clic. Seguía perdiéndose (Roma 28-9 en escritorio, Kenia 1-10 en
 * móvil: cuentas vacías, y el aviso de signup sin «Viene de probar»): el href que se
 * pinta no llevaba la prueba, y clic central, pulsación larga, «abrir en pestaña
 * nueva» o copiar el enlace no pasan por el onClick.
 *
 * Ahora tres capas, de más a menos fiable:
 *  1. el href SIEMPRE lleva la prueba (`useSignupUrl` se actualiza al subir un documento);
 *  2. al hacer clic se recalcula por si acaso (`signupUrl`);
 *  3. una cookie de primera parte en .whappscan.com (la app, en app.whappscan.com, la
 *     lee si llega a /signup sin parámetros: otra pestaña, otro enlace, URL escrita a mano).
 * Caduca a las 48 h, la misma ventana en que `signup` acepta importar la prueba.
 */
const KEY = "ws_trial";
const COOKIE = "ws_trial";
const MAX_AGE = 48 * 3600;
const listeners = new Set();

function cookieDomain() {
  const h = window.location.hostname;
  return h === "whappscan.com" || h.endsWith(".whappscan.com") ? "; Domain=.whappscan.com" : "";
}

export function rememberTrial(template, resultId) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ template, resultId }));
  } catch {
    // Sin almacenamiento (modo privado estricto): queda la cookie.
  }
  try {
    if (/^[a-z_]{2,24}$/.test(template) && /^[0-9a-f-]{36}$/i.test(resultId)) {
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${COOKIE}=${template}.${resultId}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${cookieDomain()}${secure}`;
    }
  } catch {
    // idem
  }
  listeners.forEach((fn) => fn());
}

function readTrial() {
  try {
    const t = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (t?.resultId && t?.template) return t;
  } catch {
    // pasa a la cookie
  }
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([a-z_]{2,24})\\.([0-9a-f-]{36})`, "i"));
    if (m) return { template: m[1], resultId: m[2] };
  } catch {
    // idem
  }
  return null;
}

export function signupUrl(lang) {
  const t = readTrial();
  const extra = t ? `&tpl=${encodeURIComponent(t.template)}&from=${encodeURIComponent(t.resultId)}` : "";
  const ref = readReferral(); // «Invita y gana»: el código del amigo que lo invitó (cookie ws_ref)
  return `https://app.whappscan.com/signup?lang=${lang}${extra}${ref ? `&ref=${ref}` : ""}`;
}

/** Hook: la URL de registro para pintarla en el href, al día con la última prueba. */
export function useSignupUrl(lang) {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => signupUrl(lang),
    () => `https://app.whappscan.com/signup?lang=${lang}`
  );
}
